// #region Imports
// React and related hooks
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Material-UI components
import { Stack, Tooltip, Button } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import {
  AdminPanelSettingsOutlined,
  DeleteOutlined as DeleteIcon,
  ModeEditOutlineOutlined as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';

// FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd } from '@fortawesome/pro-regular-svg-icons';

// Custom utilities and hooks
import { GlobalVariables } from '../../globalVar';
import customAxios from '../../utils/customAxios';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { getToast } from '../../utils/toastConfig';
import { useTranslationHook } from '../../hooks/UseTranslationHook';

// Modals
import { CreateRoleModal } from '../Modal/Create/ModalAddRole';
import { UpdateRoleModal } from '../Modal/Update/ModalUpdateRole';
import { DeleteRoleModal } from '../Modal/Delete/ModalDeleteRole';
// #endregion
import { useTableLocale } from '../../hooks/useTableLocale';

export const RolesTable = () => {
  const navigate = useNavigate();
  // #region UseState
  const [isLoading, setIsLoading] = useState(false);
  const [rolesData, setRolesData] = useState([]);
  const [roleInfo, setRoleInfo] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // #endregion
  const locale = useTableLocale();

  // #region Translation Hooks
  const {
    addString,
    editString,
    deleteString,
    detailString,
    rolesString,
    roleString,
    viewPermsString,
  } = useTranslationHook();

  const { t } = useTranslation();
  // #endregion

  // #region Fetch Data
  const fetchData = async () => {
    const endpoint = `${GlobalVariables.getURL()}roles/listRoles`;

    setIsLoading(true);

    try {
      const response = await customAxios.get(endpoint, {
        headers: {
          Authorization: 'bearer ' + localStorage.getItem('token'),
        },
      });
      const rolesData = response.data.data;
      setRolesData(rolesData);
    } catch (error) {
      console.log('🚀 ~ error:', error);
      getToast({ status: 'error', title: error.response.data.message });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);
  // #endregion

  // #region Table Columns
  const rolesColumns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'role_name',
      headerName: 'Rol',
      width: 200,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      width: 300,
    },
    {
      field: 'created_by_name',
      headerName: 'Creado por',
      width: 200,
    },
    {
      field: 'created_at',
      headerName: 'Fecha de creación',
      width: 180,
    },
    {
      field: 'updated_at',
      headerName: 'Última actualización',
      width: 180,
    },
    {
      field: 'updated_by_name',
      headerName: 'Actualizado por',
      width: 200,
    },
    // Map through all columns and add default renderCell if not already defined
  ].map((col) =>
    col.renderCell
      ? col
      : {
          ...col,
          renderCell: (params) =>
            ['created_at', 'updated_at'].includes(col.field)
              ? formatDateDDMMYYYY(params.row[col.field])
              : params.row[col.field] || '-',
        },
  );
  // #endregion

  const actionColumn = [
    {
      field: 'action',
      headerName: 'Detalle',
      width: 160,
      renderCell: (params) => {
        return (
          <div className="cellAction">
            <Tooltip title={viewPermsString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={() => navigate(`/roles/permissions/${params.id}`)}
              >
                <AdminPanelSettingsOutlined
                  className="detail-icons"
                  id="permissions-icon"
                />
              </button>
            </Tooltip>
            <Tooltip title={editString} arrow>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  marginTop: '5px',
                }}
                onClick={() => {
                  setRoleInfo({});
                  setRoleInfo(params.row);
                  setShowEditModal(true);
                }}
              >
                <EditIcon className="detail-icons" id="update-icon" />
              </button>
            </Tooltip>
            <Tooltip
              title={
                params.row.is_inmutable === 1
                  ? 'Cannot delete this role'
                  : deleteString
              }
              arrow
            >
              <span>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    marginTop: '5px',
                  }}
                  onClick={() => {
                    if (params.row.is_inmutable !== 1) {
                      setRoleInfo({});
                      setRoleInfo(params.row);
                      setShowDeleteModal(true);
                    }
                  }}
                  disabled={params.row.is_inmutable === 1}
                >
                  <DeleteIcon
                    className="detail-icons"
                    id="delete-icon"
                    style={{ opacity: params.row.is_inmutable === 1 ? 0.5 : 1 }}
                  />
                </button>
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <Stack spacing={2}>
      <div className="datatable-btns-container">
        <div className="dt-btn-main-container">
          <button
            className="add-button"
            onClick={() => setShowCreateModal(true)}
          >
            {addString} {roleString} <FontAwesomeIcon icon={faAdd} />
          </button>
        </div>
      </div>

      {/* Modals */}
      <CreateRoleModal
        showModal={showCreateModal}
        setShowModal={setShowCreateModal}
        refreshData={fetchData}
      />

      <UpdateRoleModal
        roleInfo={roleInfo}
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        refreshData={fetchData}
      />

      <DeleteRoleModal
        roleInfo={roleInfo}
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        refreshData={fetchData}
      />

      <DataGridPro
        localeText={locale.components.MuiDataGrid.defaultProps.localeText}
        rows={rolesData}
        columns={rolesColumns.concat(actionColumn)}
        loading={isLoading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'id', sort: 'desc' }],
          },
          columns: {
            columnVisibilityModel: {
              id: false,
            },
          },
          pinnedColumns: { right: ['action'] },
          pagination: {
            pageSize: 5,
          },
        }}
        disableSelectionOnClick
        checkboxSelection
        pagination
        pageSizeOptions={[5, 10, 25, 50, 100]}
        slots={{
          toolbar: GridToolbar,
          noRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              {t('No roles found')}
            </Stack>
          ),
        }}
      />
    </Stack>
  );
};
